import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import type { ControlResult, ControlStatusType } from "@/types/compliance";

interface ControlListProps {
  controls: ControlResult[];
  isLoading: boolean;
}

const statusConfig: Record<
  ControlStatusType,
  { label: string; className: string }
> = {
  met: { label: "Met", className: "bg-green-500/20 text-green-400" },
  partially_met: {
    label: "Partial",
    className: "bg-yellow-500/20 text-yellow-400",
  },
  not_met: { label: "Not Met", className: "bg-red-500/20 text-red-400" },
  not_applicable: {
    label: "N/A",
    className: "bg-slate-500/20 text-slate-400",
  },
};

export function ControlList({ controls, isLoading }: ControlListProps) {
  const [filter, setFilter] = useState("");

  if (isLoading) {
    return (
      <div className="space-y-2">
        {[1, 2, 3].map((i) => (
          <div key={i} className="h-10 animate-pulse rounded bg-muted" />
        ))}
      </div>
    );
  }

  const filtered = controls.filter(
    (c) =>
      c.control_name.toLowerCase().includes(filter.toLowerCase()) ||
      c.control_id.toLowerCase().includes(filter.toLowerCase()) ||
      c.category.toLowerCase().includes(filter.toLowerCase()),
  );

  return (
    <div className="space-y-3">
      <input
        type="text"
        placeholder="Filter controls..."
        value={filter}
        onChange={(e) => setFilter(e.target.value)}
        className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
      />
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-24">ID</TableHead>
              <TableHead>Name</TableHead>
              <TableHead>Category</TableHead>
              <TableHead className="w-20">Status</TableHead>
              <TableHead className="w-20 text-right">Score</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} className="text-center text-muted-foreground">
                  No controls match the filter.
                </TableCell>
              </TableRow>
            ) : (
              filtered.map((c) => {
                const cfg = statusConfig[c.status];
                return (
                  <TableRow key={c.control_id}>
                    <TableCell className="font-mono text-xs">
                      {c.control_id}
                    </TableCell>
                    <TableCell>{c.control_name}</TableCell>
                    <TableCell className="text-muted-foreground text-sm">
                      {c.category}
                    </TableCell>
                    <TableCell>
                      <Badge className={cfg.className}>{cfg.label}</Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      {Math.round(c.score * 100)}%
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
